import { useCallback, useEffect, useRef, useState } from 'react';
import { getPipelineIndexed } from '@/services/pipeline.service.js';
import { evictStale } from '@/lib/inFlight.js';
import useIdleAware, { IDLE_POLL_MS } from '@/hooks/useIdleAware.js';

// 60s background poll while foregrounded. Pauses on visibilitychange
// (spec §3.7); drops to 10-min cadence when visible but idle >15 min.
// Mutations should call refresh() directly for action-driven updates.
const POLL_MS = 60_000;

// scope (all | mine | unassigned) is decided on the server (deals-door-1 T19). A new scope drops the last answer, so a
// board read under All is never shown under Mine. enabled false holds every read (the page waits for /auth/me).
export default function usePipeline({ includeClosed = false, scope = 'all', enabled = true } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const aborted = useRef(false);
  const timerRef = useRef(null);
  const lastFetchRef = useRef(0);
  const seqRef = useRef(0);
  const scopeRef = useRef(scope);

  // load({ poll: true }) only from the timer's tick (X-Tracon-Poll, lib/poll.js); refresh() is a person's read.
  // Only the latest read lands (seq): an All read still in flight when the scope turns to Mine is dropped.
  const load = useCallback(async ({ poll = false } = {}) => {
    if (!enabled) return;
    const mine = ++seqRef.current;
    lastFetchRef.current = Date.now();
    const current = () => !aborted.current && mine === seqRef.current;
    try {
      const d = await getPipelineIndexed({ includeClosed, poll, scope });
      if (current()) {
        setData(d);
        setError(null);
      }
    } catch (err) {
      if (current()) setError(err);
    } finally {
      if (current()) setLoading(false);
    }
  }, [includeClosed, scope, enabled]);
  const refresh = useCallback(() => load(), [load]);

  useEffect(() => {
    aborted.current = false;
    if (scopeRef.current !== scope) {
      scopeRef.current = scope;
      setData(null);
      setError(null);
      setLoading(true);
    }
    refresh();
    return () => {
      aborted.current = true;
    };
  }, [refresh, scope]);

  const isIdle = useIdleAware({
    onActive: () => {
      if (Date.now() - lastFetchRef.current > POLL_MS) refresh();
    },
  });

  useEffect(() => {
    function tick() {
      if (document.visibilityState !== 'visible') return;
      if (isIdle() && Date.now() - lastFetchRef.current < IDLE_POLL_MS) return;
      load({ poll: true });
    }
    function start() {
      if (timerRef.current) return;
      timerRef.current = setInterval(tick, POLL_MS);
    }
    function stop() {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    function onVisibility() {
      if (document.visibilityState === 'visible') {
        // Wake from sleep: drop dedup slots whose promises died with the
        // socket, so this refresh fetches instead of awaiting a corpse.
        evictStale();
        refresh();
        start();
      } else {
        stop();
      }
    }
    start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refresh, load, isIdle]);

  return { data, error, loading, refresh };
}
